import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  UpdateAssessmentDto,
  AssessmentDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityAssessmentsApi } from "@api/services";

export const ACTION_UPDATE_ASSESSMENT = "UPDATE_ASSESSMENT";
const ACTION_GET_ASSESSMENTS = "GET_ASSESSMENTS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface UpdateAssessmentRequest {
  id: string;
  data: UpdateAssessmentDto;
}

interface UpdateAssessmentResponse {
  data: AssessmentDto;
}

const updateAssessment = async (
  request: UpdateAssessmentRequest,
): Promise<UpdateAssessmentResponse> => {
  const response =
    await doubleMaterialityAssessmentsApi.doubleMaterialityAssessmentsControllerUpdateAssessment(
      request.id,
      request.data,
    );

  return {
    data: response.data,
  };
};

export const useUpdateAssessment = (
  user: UserProfileDto | null,
): {
  updateAssessment: (request: UpdateAssessmentRequest) => void;
  updateAssessmentAsync: (
    request: UpdateAssessmentRequest,
  ) => Promise<UpdateAssessmentResponse>;
  isPendingUpdateAssessment: boolean;
  isSuccessUpdateAssessment: boolean;
  isErrorUpdateAssessment: boolean;
  errorUpdateAssessment: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: updateAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_ASSESSMENTS],
      });
      enqueueSnackbar(t("assessments.messages.updateSuccess"), {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("assessments.messages.updateError"), {
        variant: "error",
      });
    },
  });

  return {
    updateAssessment: mutation.mutate,
    updateAssessmentAsync: mutation.mutateAsync,
    isPendingUpdateAssessment: mutation.isPending,
    isSuccessUpdateAssessment: mutation.isSuccess,
    isErrorUpdateAssessment: mutation.isError,
    errorUpdateAssessment: mutation.error,
    enabled,
  };
};
